import * as React from "react";
import type { Evidence } from "@/types/evidences";
import { BusinessReportOutcomeBreadcrumbs } from "./businessReportOutcomeBreadcrumbs";
import { BusinessReportOutcomeWebsocket } from "./businessReportOutcomeWebsocket";
import { BusinessReportOutcomeRow } from "./businessReportOutcomeRow";
import { BusinessReportOutcomeHeaderSection } from "./businessReportOutcomeHeaderSection";
import { BusinessReportOutcomeNotes } from "./components/BusinessReportOutcomeNotes";
import {
  useGetBusinessReportOutcomeQuery,
  useGetFamilySourceOutcomesQuery,
  useGetSnapshotEventsQuery,
} from "../businessReports.api";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { isFamilySummaryOutcome } from "@/modules/snapshots/businessReports.types";
import * as analytics from "@/services/analytics";
import { Sparkles, AlertCircle } from "lucide-react";
import { Loader } from "@/components/ui/loader";
import { ModifySnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/modifySnapshotEventDialog";
import { ScrollableChat } from "@/components/feature/chat";
import type { FamilySource, ReportPanelType } from "./types";
import { citationEvidencesFor, outcomeEvidences } from "./outcomeEvidences";
export type { PromptResult } from "./businessReportOutcomeRow/types";
import type { PromptResult } from "./businessReportOutcomeRow/types";
import { isEvidenceForPrompt } from "@/modules/snapshots/grid/sidebar/sourceDocument";
import { isSnapshotCellPending } from "@/modules/snapshots/grid/cellText";

interface Props {
  outcomeId: string;
  isPanelOpen: boolean;
  onPanelToggle: (panel: Exclude<ReportPanelType, null>) => void;
}

export const BusinessReportOutcomeContent: React.FC<Props> = ({ outcomeId, isPanelOpen, onPanelToggle }) => {
  const { data: businessReportOutcome = null, refetch } = useGetBusinessReportOutcomeQuery(outcomeId);
  const [isComplete, setIsComplete] = React.useState(false);
  const [evidences, setEvidences] = React.useState<Evidence[]>([]);
  const [isCreateSnapshotEventDialogOpen, setIsCreateSnapshotEventDialogOpen] = React.useState(false);

  const [promptsResult, setPromptsResult] = React.useState<PromptResult[]>([]);

  const { data: familySourceOutcomes } = useGetFamilySourceOutcomesQuery(outcomeId, {
    skip: !isFamilySummaryOutcome(businessReportOutcome),
  });
  const familySources = React.useMemo<FamilySource[]>(
    () => familySourceOutcomes?.map(outcome => ({ outcome, evidences: outcomeEvidences(outcome) })) ?? [],
    [familySourceOutcomes],
  );
  const citationEvidences = React.useMemo(
    () => citationEvidencesFor(evidences, familySources),
    [evidences, familySources],
  );
  const { currentPdfId, setCurrentPdfId } = useCurrentPdf();

  // A contract summary has no document of its own: open the first source document until a citation picks one.
  const firstSourceDocumentId = familySourceOutcomes?.find(outcome => outcome.fs_entry)?.fs_entry?.id ?? null;
  React.useEffect(() => {
    if (firstSourceDocumentId && !currentPdfId) {
      setCurrentPdfId(firstSourceDocumentId);
    }
  }, [firstSourceDocumentId, currentPdfId, setCurrentPdfId]);

  React.useEffect(() => {
    setIsComplete(businessReportOutcome?.status === "COMPLETED" || businessReportOutcome?.status === "CANCELLED");
  }, [businessReportOutcome?.status]);

  React.useEffect(() => {
    if (businessReportOutcome?.name) {
      analytics.snapshotView(businessReportOutcome.name);
    }
  }, [businessReportOutcome?.name]);

  const inProgress = businessReportOutcome?.status === "NEW" || businessReportOutcome?.status === "IN_PROGRESS";

  const hasPendingAiCells = React.useMemo(
    () => !!businessReportOutcome && businessReportOutcome.result.some(r => !r.is_note && isSnapshotCellPending(r)),
    [businessReportOutcome],
  );

  React.useEffect(() => {
    if (!isComplete || !hasPendingAiCells) return;
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      refetch();
      if (attempts >= 40) window.clearInterval(interval);
    }, 4000);
    return () => window.clearInterval(interval);
  }, [isComplete, hasPendingAiCells, refetch]);

  const onPromptTypewriterEnded = React.useCallback(
    (prompt: PromptResult) => {
      const index = promptsResult.findIndex(row => row.business_report_prompt_id === prompt.business_report_prompt_id);

      const nextPrompt = promptsResult[index + 1];
      if (nextPrompt) {
        setPromptsResult(prev =>
          prev.map(row => ({
            ...row,
            processing: row.business_report_prompt_id === nextPrompt.business_report_prompt_id,
            show: row.business_report_prompt_id === nextPrompt.business_report_prompt_id ? true : row.show,
          })),
        );
      }
    },
    [setPromptsResult, promptsResult],
  );

  const { data: events = [] } = useGetSnapshotEventsQuery({
    businessReportOutcomeId: outcomeId,
  });

  const eventsByResultId = React.useMemo(
    () =>
      events.reduce(
        (acc, event) => {
          const resultId = event.business_report_outcome_result_id;
          if (resultId) {
            if (!acc[resultId]) {
              acc[resultId] = [];
            }
            acc[resultId].push(event);
          }
          return acc;
        },
        {} as Record<string, typeof events>,
      ),
    [events],
  );

  const hasBusinessReportOutcomeEvent = events.some(
    row => row.business_report_outcome_id && !row.business_report_outcome_result_id,
  );

  return (
    <div className="flex flex-grow flex-col overflow-hidden">
      <div className="flex-shrink-0">
        <BusinessReportOutcomeBreadcrumbs businessReportOutcome={businessReportOutcome} />
        <BusinessReportOutcomeHeaderSection
          businessReportOutcome={businessReportOutcome}
          isComplete={isComplete}
          inProgress={inProgress}
          businessReportOutcomeId={outcomeId}
          setPromptsResult={setPromptsResult}
          setEvidences={setEvidences}
          hasEvent={hasBusinessReportOutcomeEvent}
          onCreateEventClick={() => {
            analytics.snapshotCreateEvent();
            setIsCreateSnapshotEventDialogOpen(true);
          }}
          isPanelOpen={isPanelOpen}
          onPanelToggle={onPanelToggle}
        />
      </div>

      <div className="min-h-0 flex-1">
        {/* Follow only a running generation; isComplete trails the status by a render and would scroll a finished outcome down. */}
        <ScrollableChat className="pb-6" autoScrollOnNewContent={inProgress}>
          {businessReportOutcome && (
            <BusinessReportOutcomeWebsocket
              setEvidences={setEvidences}
              setIsComplete={setIsComplete}
              refetch={refetch}
              businessReportOutcome={businessReportOutcome}
              setPromptsResult={setPromptsResult}
            />
          )}
          {businessReportOutcome &&
            promptsResult
              .filter(
                row =>
                  row.show && (isComplete || row.content || row.is_date || row.error_message || row.relevancy != null),
              )
              .map(promptResult => (
                <BusinessReportOutcomeRow
                  businessReportOutcome={businessReportOutcome}
                  businessReportOutcomeResultId={promptResult.business_report_outcome_result_id}
                  onPromptTypewriterEnded={onPromptTypewriterEnded}
                  evidences={
                    promptResult.processing
                      ? []
                      : citationEvidences.filter(evidence =>
                          isEvidenceForPrompt(
                            evidence.business_report_prompt_id ?? null,
                            promptResult.business_report_prompt_id,
                          ),
                        )
                  }
                  content={promptResult.content}
                  promptResult={promptResult}
                  events={eventsByResultId[promptResult.business_report_outcome_result_id] || []}
                  processing={promptResult.processing}
                  key={promptResult.business_report_outcome_result_id}
                  outcomeId={outcomeId}
                  familySources={familySources}
                />
              ))}

          {isComplete && businessReportOutcome && (
            <BusinessReportOutcomeNotes businessReportOutcome={businessReportOutcome} />
          )}

          {!isComplete && !businessReportOutcome?.status?.includes("FAILED") && (
            <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-[#ddd5cb] bg-[#fffdfb] p-8">
              <Sparkles className="mb-3 h-8 w-8 animate-pulse text-coral" />
              <div className="mb-2 flex items-center gap-2">
                <Loader className="h-5 w-5 text-coral" />
                <span className="text-base font-medium text-coral">Preparing next section...</span>
              </div>
              <p className="text-center text-sm font-medium text-gray-500">
                {promptsResult.filter(p => p.show).length} of {promptsResult.length} sections generated
              </p>
            </div>
          )}
          {businessReportOutcome?.status === "FAILED" && (
            <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-[#e7b4ad] bg-[#fffdfb] p-8">
              <AlertCircle className="mb-3 h-8 w-8 text-destructive" />
              <p className="mb-1 text-base font-medium text-destructive">Report processing failed</p>
              <p className="text-center text-sm font-medium text-gray-500">
                {businessReportOutcome.error_message ||
                  "Please try regenerating the report or contact support if the issue persists."}
              </p>
            </div>
          )}
        </ScrollableChat>
      </div>

      {isCreateSnapshotEventDialogOpen && businessReportOutcome && (
        <ModifySnapshotEventDialog
          mode="create"
          open={isCreateSnapshotEventDialogOpen}
          onClose={() => setIsCreateSnapshotEventDialogOpen(false)}
          businessReportOutcomeResultId={null}
          businessReportOutcomeId={businessReportOutcome.id}
          eventType="Document"
        />
      )}
    </div>
  );
};
